import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Laptop, PackagePlus, Settings2, UserMinus, UserPlus } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { ConfirmDeliveryButton } from '@/components/office-manager/ConfirmDeliveryButton';
import { OmBajaDeviceActions } from '@/components/office-manager/OmBajaDeviceActions';
import { OmAddDeviceDialog } from '@/components/office-manager/OmAddDeviceDialog';
import { DeviceStatusBadge } from '@/components/inventory/DeviceStatusBadge';
import { OfficeManagerGovernancePanel } from '@/components/office-manager/OfficeManagerGovernancePanel';
import { formatDayMonth } from '@/../../shared/dates';
import type { OfficeKey } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';

const OFFICE_LABELS: Record<OfficeKey, string> = {
  buenosAires: 'Office Buenos Aires',
  cordoba: 'Office Córdoba',
  brasil: 'Office Brasil',
  colombia: 'Office Colombia',
  sinOffice: 'Sin office',
};

// Badge unificado del inventario (misma paleta en tabla, board y métricas)

const RECUPERO_LABEL: Record<string, string> = {
  no_recovery: 'Quedó en oficina',
  managed_quipteams: 'Retiro Quipteams pedido',
  managed_whatsapp: 'Gestionado por WhatsApp',
  pending: 'Pendiente',
};

export function OfficeManagerBoard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');
  const [addOpen, setAddOpen] = useState(false);
  const [govOpen, setGovOpen] = useState(false);
  // 'week' = semana actual · 'all' = histórico del año (mismas acciones en ambas)
  const [scope, setScope] = useState<'week' | 'all'>('week');

  const { data: access } = trpc.officeManager.getMyAccess.useQuery(undefined, { staleTime: 5 * 60 * 1000 });
  const { data: board, isLoading, error } = trpc.officeManager.getBoard.useQuery({ scope });
  const isWeek = scope === 'week';

  // "Sin office" no es una oficina física: sin oficina propia no se cargan equipos
  const canAddDevice = (access?.offices ?? []).some((o) => o !== 'sinOffice');

  const offices = board?.offices ?? [];
  const defaultTab = offices.find((o) => o.altas.length + o.bajas.length > 0)?.office ?? offices[0]?.office;

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full border p-0.5">
            <button
              onClick={() => setScope('week')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                isWeek ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('office-manager.officemanagerboard.esta_semana')}
            </button>
            <button
              onClick={() => setScope('all')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                !isWeek ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('office-manager.officemanagerboard.historico')}
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            {isWeek
              ? (board
                  ? t('office-manager.officemanagerboard.ingresos_y_bajas_de_la_semana_rango', { from: formatDayMonth(board.weekStart), to: formatDayMonth(board.weekEnd) })
                  : t('office-manager.officemanagerboard.ingresos_y_bajas_de_la_semana'))
              : t('office-manager.officemanagerboard.todos_los_ingresos_y_bajas_del')}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" size="icon" title={t('office-manager.officemanagerboard.office_managers_por_oficina')} onClick={() => setGovOpen(true)}>
              <Settings2 className="h-4 w-4" />
            </Button>
          )}
          {canAddDevice && (
            <Button onClick={() => setAddOpen(true)}>
              <PackagePlus className="mr-2 h-4 w-4" />
              {t('office-manager.officemanagerboard.agregar_equipo')}
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center p-12">
          <WinclapLoader size={48} className="text-foreground/25" />
        </Card>
      ) : error ? (
        <Card className="flex flex-col items-center justify-center p-12 space-y-2">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <p className="font-semibold">{t('office-manager.officemanagerboard.no_se_pudo_cargar_el_tablero')}</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </Card>
      ) : offices.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">{t('office-manager.officemanagerboard.sin_oficinas_asignadas')}</Card>
      ) : (
        <Tabs defaultValue={defaultTab} key={defaultTab}>
          <TabsList className="flex-wrap h-auto">
            {offices.map((o) => (
              <TabsTrigger key={o.office} value={o.office}>
                {OFFICE_LABELS[o.office as OfficeKey]}
                <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 text-xs">
                  {o.altas.length + o.bajas.length}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {offices.map((o) => (
            <TabsContent key={o.office} value={o.office} className="mt-4 space-y-6">
              {/* Ingresos */}
              <Card>
                <div className="flex items-center gap-2 border-b p-4">
                  <UserPlus className="h-4 w-4 text-blue-500" />
                  <h3 className="font-semibold">{isWeek ? t('office-manager.officemanagerboard.ingresos_esta_semana') : t('office-manager.officemanagerboard.ingresos_del_ano')}</h3>
                  <Badge variant="secondary">{o.altas.length}</Badge>
                </div>
                {o.altas.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">{isWeek ? t('office-manager.officemanagerboard.sin_ingresos_esta_semana') : t('office-manager.officemanagerboard.sin_ingresos_en_el_ano')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Correo</TableHead>
                          <TableHead>{t('office-manager.officemanagerboard.equipo_asignado')}</TableHead>
                          <TableHead>Entrega</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {o.altas.map((alta: any) => (
                          <TableRow key={alta.idAlta}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {alta.fechaIngresoParsed ? formatDayMonth(alta.fechaIngresoParsed.slice(0, 10)) : alta.fechaIngreso}
                            </TableCell>
                            <TableCell className="font-medium">{alta.nombre}</TableCell>
                            <TableCell className="text-sm font-mono">{alta.correoWinclap || '—'}</TableCell>
                            <TableCell>
                              {alta.devices.length === 0 ? (
                                <span className="text-xs text-muted-foreground">{t('office-manager.officemanagerboard.sin_equipo_asignado')}</span>
                              ) : (
                                <div className="space-y-1">
                                  {alta.devices.map((d: any) => (
                                    <div key={d.deviceId} className="flex items-center gap-2 text-sm">
                                      <Laptop className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>{d.modelo}</span>
                                      <span className="font-mono text-xs text-muted-foreground">{d.nroSerie}</span>
                                      <DeviceStatusBadge status={d.status} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {alta.devices.map((d: any) => (
                                  <div key={d.deviceId}>
                                    <ConfirmDeliveryButton device={d} clapperName={alta.nombre} />
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>

              {/* Bajas */}
              <Card>
                <div className="flex items-center gap-2 border-b p-4">
                  <UserMinus className="h-4 w-4 text-amber-500" />
                  <h3 className="font-semibold">{isWeek ? t('office-manager.officemanagerboard.bajas_esta_semana') : t('office-manager.officemanagerboard.bajas_del_ano')}</h3>
                  <Badge variant="secondary">{o.bajas.length}</Badge>
                </div>
                {o.bajas.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">{isWeek ? t('office-manager.officemanagerboard.sin_bajas_esta_semana') : t('office-manager.officemanagerboard.sin_bajas_en_el_ano')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Clapper</TableHead>
                          <TableHead>Equipos</TableHead>
                          <TableHead>{t('office-manager.officemanagerboard.que_hacer_con_la_compu')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {o.bajas.map((baja: any) => (
                          <TableRow key={baja.idBaja}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {baja.fechaBajaParsed ? formatDayMonth(baja.fechaBajaParsed.slice(0, 10)) : baja.fechaBaja}
                            </TableCell>
                            <TableCell>
                              <p className="font-medium">{baja.nombre || baja.correo}</p>
                              {baja.nombre && <p className="text-xs text-muted-foreground">{baja.correo}</p>}
                            </TableCell>
                            <TableCell>
                              {baja.devices.length === 0 ? (
                                <span className="text-xs text-muted-foreground">{t('office-manager.officemanagerboard.sin_equipos_a_su_nombre')}</span>
                              ) : (
                                <div className="space-y-1">
                                  {baja.devices.map((d: any) => (
                                    <div key={d.deviceId} className="flex items-center gap-2 text-sm">
                                      <Laptop className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>{d.modelo}</span>
                                      <span className="font-mono text-xs text-muted-foreground">{d.nroSerie}</span>
                                      <DeviceStatusBadge status={d.status} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {baja.recupero ? (
                                <Badge variant="secondary">
                                  {RECUPERO_LABEL[baja.recupero.status] || baja.recupero.status}
                                </Badge>
                              ) : (
                                <div className="space-y-1.5">
                                  {baja.devices.map((d: any) => (
                                    <OmBajaDeviceActions key={d.deviceId} device={d} baja={baja} />
                                  ))}
                                  {baja.devices.length === 0 && (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <OmAddDeviceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        offices={(access?.offices ?? []) as OfficeKey[]}
      />
      {isAdmin && <OfficeManagerGovernancePanel open={govOpen} onClose={() => setGovOpen(false)} />}
    </div>
  );
}
